import { emit } from '@tauri-apps/api/event';

import { logger } from '@/utils/logger/logger';
import { focusTauriWindowByLabel, RPA_MAIN_WINDOW_LABEL } from '@heidi/plugin-core';
import { isTauri } from '@heidi/runtime/platform';

/**
 * Tauri event + route helpers for jumping from the floating dictation widget
 * back to the main Heidi window's sign-in step.
 *
 * Pattern mirrors `openProjectInHeidi.ts`: the widget webview emits a Tauri
 * event and focuses the main window; the main window
 * (`useMainWindowDictationNavigationListener`) listens and pushes the dictate
 * onboarding sign-in route. Used when the anonymous dictation quota is
 * exhausted and the user clicks the sign-in prompt bubble.
 */

export const DICTATION_OPEN_SIGN_IN_EVENT = 'dictation:open-sign-in';

/** Dictate onboarding flow — its sign-in step is the destination for quota-blocked users. */
export const DICTATION_SIGN_IN_HREF = '/dictate-onboarding';
export const DICTATION_SIGN_IN_STEP = 'sign-in' as const;

export function getDictationSignInHref(): string {
  return `${DICTATION_SIGN_IN_HREF}?step=${DICTATION_SIGN_IN_STEP}`;
}

async function focusMainWindow(): Promise<void> {
  try {
    await focusTauriWindowByLabel(RPA_MAIN_WINDOW_LABEL);
  } catch (error) {
    logger.debug('[dictation/open-sign-in] focus main window failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * From the widget: focus the main window and ask it to navigate to the
 * dictate onboarding sign-in step. No-op outside Tauri.
 */
export async function emitOpenDictationSignIn(): Promise<void> {
  if (!isTauri()) return;

  try {
    await emit(DICTATION_OPEN_SIGN_IN_EVENT);
  } catch (error) {
    logger.warn('[dictation/open-sign-in] failed to emit open-sign-in event', {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  await focusMainWindow();
}
